import { convertToArray } from '@dispatch/shared';
import {
  ApiResponseWithPaging,
  DispatchInformation,
  GeneralResponse,
  Resource,
  TaskAssignmentRequest,
  TaskInfo,
  TaskListFilterGroup,
  TaskResponse,
  TasksListFilter,
  TaskSummary,
  TaskLockStatus,
  TaskStatusRequest,
  ITaskMessage,
  TaskType,
  DispatchTaskAssignmentView,
  TaskStatus,
  CWSSummary,
  UIValidationRulesResponse,
  TaskFilterGroup,
  CollapseNonWorkingTimeConfig,
  BulkTaskLockResponse,
  NaqRetryResponse,
  BulkLockPaginationUpperLimitConfig,
  TaskInclude,
} from '@dispatch/shared/types';
import { GridSortModel } from '@mui/x-data-grid-pro';
import axios from 'axios';
import { dispatchClient } from '../clients';
import { mapResponseWithPaging } from '../helpers/AxiosMappers';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';
import { parseSort } from './helpers';
import invariant from 'tiny-invariant';
import { getResource } from '../resources/resource.service';

const APIM_SERVICE_TASKS_URL = 'dispatch/service/tasks';
const APIM_SERVICE_URL = 'dispatch/service';
const APIM_SERVICE_MOI_URL = (tenantId: string) =>
  `configuration/${tenantId}/methodOfInspections`;
const CWS_SUMMARY_URL = (claimId: string) =>
  `dispatch/service/claims/cws/${claimId}`;
const APIM_SERVICE_QUALIFIED_RESOURCES_FNOL_URL = (tenantId: string) =>
  `configuration/${tenantId}/feature:qualified-resources-fnol`;
const APIM_SERVICE_NAQ_SELF_SERVICE_URL = (tenantId: string) =>
  `configuration/${tenantId}/feature:NaqSelfServiceResolution`;
const APIM_SERVICE_NAQ_RETRY_URL = (taskId: string) =>
  `dispatch/service/tasks/${taskId}/status/naq-retry`;
const APIM_SERVICE_UI_VALIDATION_RULES_URL = (tenantId: string) =>
  `configuration/${tenantId}/uiValidationRules`;
const APIM_SERVICE_COLLAPSE_NON_WORKING_TIME_CONFIG_URL = (tenantId: string) =>
  `configuration/${tenantId}/feature:collapse-non-working-time-config`;
const APIM_SERVICE_Trim_Leading_Zeros_URL = (tenantId: string) =>
  `configuration/${tenantId}/feature:trim-leading-zeros`;
const APIM_SERVICE_BULK_LOCK_PAGINATION_UPPER_LIMIT_URL = (tenantId: string) =>
  `configuration/${tenantId}/bulk_lock_pagination_upper_limit`;

export async function getTaskAssignments(
  taskId: string
): Promise<DispatchTaskAssignmentView[] | undefined> {
  try {
    const response = await dispatchClient.get<DispatchTaskAssignmentView[]>(
      `${APIM_SERVICE_URL}/taskAssignments`,
      {
        params: {
          pageSize: 50,
          taskIds: taskId,
        },
      }
    );

    const assignmentTasks: DispatchTaskAssignmentView[] = response?.data;

    const withResource = await Promise.all(
      assignmentTasks.map(async (taskAssignment) => {
        if (!taskAssignment.assignedResourceId?.current) {
          return taskAssignment;
        }
        const resource = await getResource(
          taskAssignment.assignedResourceId?.current
        );

        return {
          ...taskAssignment,
          assignedResource: resource?.name,
        };
      })
    );

    if (response.status === 200 && withResource) {
      return withResource;
    }
  } catch {
    throw new Error('Error fetching task from API');
  }
  return undefined;
}

/**
 * Query params for the opt-in timeline fields, as a comma-separated `include`.
 * DispatchService binds it from the query string only, so it never goes in a
 * POST body; when nothing is requested nothing is added, keeping today's
 * request unchanged.
 */
const taskIncludeParams = (include: TaskInclude[] = []): { include?: string } =>
  include.length > 0 ? { include: include.join(',') } : {};

export async function getTaskById(
  taskId: string,
  resourceId?: string,
  include: TaskInclude[] = []
): Promise<any | undefined> {
  try {
    const response = await dispatchClient.get<TaskSummary>(
      `${APIM_SERVICE_TASKS_URL}/${taskId}/details`,
      {
        params: {
          resourceId: resourceId,
          ...taskIncludeParams(include),
        },
      }
    );

    const apiTask: TaskSummary = response?.data;

    if (response.status === 200 && apiTask) {
      return { ...apiTask };
    }
  } catch {
    throw new Error('Error fetching task from API');
  }
  return undefined;
}

export async function getTask(
  taskId: string,
  resourceId?: string,
  include: TaskInclude[] = []
): Promise<TaskResponse | undefined> {
  try {
    const taskResponse: TaskSummary = await getTaskById(
      taskId,
      resourceId,
      include
    );
    const resource = taskResponse?.$msg?.assignedResource || null;

    if (taskResponse) {
      return {
        ...taskResponse,
        resource,
      };
    }
  } catch (e) {
    throw new Error('Error fetching task from API');
  }
  return undefined;
}

export async function getUnpaginatedTaskInfo(
  filters: TasksListFilter,
  sorting?: GridSortModel[number] | null
) {
  try {
    let queryFilters = filters;

    if (sorting) {
      const sort = parseSort(sorting);
      if (sort) {
        queryFilters = { ...filters, ...sort };
      }
    }

    const response = await dispatchClient.post<TaskInfo[]>(
      `${APIM_SERVICE_TASKS_URL}`,
      {
        ...queryFilters,
        assignedResourceIds: queryFilters?.assignedResourceIds,
        lossCounties: queryFilters?.lossCounties?.map((county) => county.value),
        resourceCounties: queryFilters?.resourceCounties?.map(
          (county) => county.value
        ),
      },
      {
        headers: {
          'Continuation-Token': '',
        },
      }
    );

    if (response.status === 200) {
      return response.data;
    } else {
      return [];
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    throw new Error('Error fetching incomplete tasks');
  }
}

export type TaskInfoWithAddress = TaskInfo & { location?: string };

const parseLocation = (data: TaskInfo[]): TaskInfoWithAddress[] => {
  const dataWithLocation: TaskInfoWithAddress[] = data;
  const parsedLocation = dataWithLocation.map((task) => {
    const locationArray = [];

    if (task.lossCity) locationArray.push(task.lossCity);
    if (task.lossState) locationArray.push(task.lossState);
    if (task.lossZip) locationArray.push(task.lossZip);
    if (task.lossCounty) locationArray.push(task.lossCounty);

    const location = locationArray.length > 0 ? locationArray.join(', ') : '-';

    return { ...task, location };
  });

  return parsedLocation || [];
};

export async function getTaskInfos(
  filters: TasksListFilter,
  continuationToken?: string,
  sorting?: GridSortModel[number] | null,
  include: TaskInclude[] = []
): Promise<ApiResponseWithPaging<TaskInfoWithAddress[]>> {
  try {
    let queryFilters = filters;

    if (sorting) {
      const sort = parseSort(sorting);
      if (sort) {
        queryFilters = { ...filters, ...sort };
      }
    }

    const response = await dispatchClient.post<TaskInfo[]>(
      `${APIM_SERVICE_TASKS_URL}`,
      mapFilters(queryFilters),
      {
        params: {
          pageSize: filters.pageSize,
          ...taskIncludeParams(include),
        },
        headers: {
          'Continuation-Token': continuationToken || '',
        },
      }
    );

    response.data = parseLocation(response.data);

    return mapResponseWithPaging(response);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    throw new Error('Error fetching task summaries');
  }
}

/**
 * Returns true when a filter value is "empty" — null, undefined, empty string,
 * empty array, or a date-range array where every element is null.
 */
function isEmptyFilterValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value)) {
    if (value.length === 0) return true;
    if (value.every((v) => v === null || v === undefined)) return true;
  }
  return false;
}

function mapFilters(filters: TasksListFilter) {
  const mapped = {
    ...filters,
    assignedResourceIds: filters?.assignedResourceIds,
    lossCounties:
      typeof filters?.lossCounties === 'string'
        ? []
        : filters?.lossCounties?.map((county) => county.value),
    lossZipCodes: convertToArray(filters?.lossZipCodes),
    resourceCounties: filters?.resourceCounties?.map((county) => county.value),
  };

  // Strip empty values to keep the API payload clean
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(mapped)) {
    if (!isEmptyFilterValue(value)) {
      cleaned[key] = value;
    }
  }

  return cleaned;
}

export async function assignedTasks(resourceId: string) {
  const params = {
    assignedResourceIds: [resourceId],
    pageSize: 30,
  };
  try {
    const assignedTasksResponse = await dispatchClient.post<ITaskMessage[]>(
      APIM_SERVICE_TASKS_URL,
      params
    );

    if (assignedTasksResponse.status === 200) {
      return assignedTasksResponse.data;
    } else {
      return [];
    }
  } catch (e) {
    throw new Error('error fetching resource tasks from API');
  }
}

export async function unassignedTasks(resourceId?: string) {
  try {
    const params = {
      resourceId,
    };

    const assignedTasksResponse = await dispatchClient.post<ITaskMessage[]>(
      `${APIM_SERVICE_TASKS_URL}/qualified`,
      params
    );

    if (assignedTasksResponse.status === 200) {
      return assignedTasksResponse.data;
    } else {
      return [];
    }
  } catch (e) {
    throw new Error('error fetching resource tasks from API');
  }
}

export async function getUnassignedTasks(
  filters: TasksListFilter,
  include: TaskInclude[] = []
) {
  const unassignedParams = {
    ...filters,
    status: 'unassigned',
    pageSize: 300,
  };

  const params = taskIncludeParams(include);

  try {
    // Without an opt-in, keep the original two-argument call exactly.
    const unassignedTasksResponse = params.include
      ? await dispatchClient.post<TaskInfo[]>(
          APIM_SERVICE_TASKS_URL,
          unassignedParams,
          { params }
        )
      : await dispatchClient.post<TaskInfo[]>(
          APIM_SERVICE_TASKS_URL,
          unassignedParams
        );

    return unassignedTasksResponse.data;
  } catch (e) {
    throw new Error('error fetching resource tasks from API');
  }
}

const tasksRequest = async (taskId: string, data: DispatchInformation) => {
  return dispatchClient.post<{ success: boolean } | unknown>(
    `${APIM_SERVICE_TASKS_URL}/${taskId}/status/rescheduled`,
    {
      ...data,
    }
  );
};

export async function assignResourceToTasks(
  postData: TaskAssignmentRequest[]
): Promise<{ success: boolean }> {
  try {
    return Promise.all([
      ...postData.map(async (data: TaskAssignmentRequest) => {
        return tasksRequest(data.taskId, data.data);
      }),
    ])
      .then((_res) => {
        return { success: true };
      })
      .catch((_e) => {
        return { success: false };
      });
  } catch (e) {
    return { success: false };
  }
}

const handleLossDateParsing = (filter: TasksListFilter) => {
  if (filter?.lossDateFrom) {
    if (filter['lossDate']) {
      filter['lossDate'][0] = format(
        new Date(filter?.lossDateFrom),
        'yyyy-MM-dd'
      );
    }
  }

  if (filter?.lossDateTo) {
    if (filter['lossDate']) {
      filter['lossDate'][1] = format(
        new Date(filter?.lossDateTo),
        'yyyy-MM-dd'
      );
    }
  }
};

const handleNoticeDateParsing = (filter: TasksListFilter) => {
  if (filter?.noticeDateFrom) {
    if (filter['noticeDate']) {
      filter['noticeDate'][0] = format(
        new Date(filter?.noticeDateFrom),
        'yyyy-MM-dd'
      );
    }
  }

  if (filter?.noticeDateTo) {
    if (filter['noticeDate']) {
      filter['noticeDate'][1] = format(
        new Date(filter?.noticeDateTo),
        'yyyy-MM-dd'
      );
    }
  }
};

const handleAppointmentDateParsing = (filter: TasksListFilter) => {
  if (filter?.appointmentDateFrom) {
    if (filter['appointmentDate']) {
      filter['appointmentDate'][0] = format(
        new Date(filter?.appointmentDateFrom),
        'yyyy-MM-dd'
      );
    }
  }

  if (filter?.appointmentDateTo) {
    if (filter['appointmentDate']) {
      filter['appointmentDate'][1] = format(
        new Date(filter?.appointmentDateTo),
        'yyyy-MM-dd'
      );
    }
  }
};

const handleReceivedDateParsing = (filter: TasksListFilter) => {
  if (filter?.receivedDateFrom) {
    if (filter['receivedDate']) {
      filter['receivedDate'][0] = format(
        new Date(filter?.receivedDateFrom),
        'yyyy-MM-dd'
      );
    }
  }

  if (filter?.receivedDateTo) {
    if (filter['receivedDate']) {
      filter['receivedDate'][1] = format(
        new Date(filter?.receivedDateTo),
        'yyyy-MM-dd'
      );
    }
  }
};

export async function getTaskFilters(userId: string) {
  try {
    const taskFilterResponse = await dispatchClient.get<TasksListFilter[]>(
      `${APIM_SERVICE_URL}/taskfilters`,
      {
        headers: {
          'Sys-UserID': userId, // will go away when api is updated
        },
      }
    );

    const taskFilterData = taskFilterResponse?.data;

    if (!taskFilterData) {
      return undefined;
    } else if (taskFilterResponse.status === 200 && taskFilterData) {
      taskFilterData.forEach((filter) => {
        filter['lossDate'] = [null, null];
        filter['appointmentDate'] = [null, null];
        filter['noticeDate'] = [null, null];
        filter['receivedDate'] = [null, null];

        handleLossDateParsing(filter);
        handleNoticeDateParsing(filter);
        handleAppointmentDateParsing(filter);
        handleReceivedDateParsing(filter);

        return filter;
      });
      return taskFilterData;
    }
  } catch (e) {
    throw new Error('error fetching task filters from API');
  }
  return undefined;
}

export async function getTaskFilterGroups(userId: string) {
  try {
    const taskFilterResponse = await dispatchClient.get<any>(
      `${APIM_SERVICE_URL}/groups`,
      {
        headers: {
          'Sys-UserID': userId, // will go away when api is updated
        },
      }
    );

    const taskFilterGroupData = taskFilterResponse?.data;

    if (!taskFilterGroupData) {
      return undefined;
    } else if (taskFilterResponse.status === 200 && taskFilterGroupData) {
      return taskFilterGroupData;
    }
  } catch (e) {
    throw new Error('error fetching task filters from API');
  }
  return undefined;
}

export async function upsertTaskFilters(
  filters: TaskListFilterGroup,
  userId: string
): Promise<{ success: boolean; message?: string } | undefined> {
  try {
    const id = filters?.filterId || uuidv4();
    await dispatchClient.put<TasksListFilter>(
      `${APIM_SERVICE_URL}/taskfilters/${id}`,
      filters,
      {
        headers: {
          'Sys-UserID': userId,
        },
      }
    );

    return { success: true };
  } catch (e) {
    const error: Error = e as Error;
    return { success: false, message: error?.message || 'an error occurred' };
  }
}

export async function orderIndexBulkUpdate(
  filters: Omit<TasksListFilter, 'pageSize'>[],
  userId: string
): Promise<{ success: boolean; message?: string } | undefined> {
  try {
    await dispatchClient.put<TasksListFilter[]>(
      `${APIM_SERVICE_URL}/filterOrderIndexBulkUpdate`,
      filters,
      {
        headers: {
          'Sys-UserID': userId,
        },
      }
    );

    return { success: true };
  } catch (e) {
    const error: Error = e as Error;
    return { success: false, message: error?.message || 'an error occurred' };
  }
}

export async function groupsOrderIndexBulkUpdate(
  groups: TaskFilterGroup[],
  userId: string
): Promise<{ success: boolean; message?: string } | undefined> {
  try {
    await dispatchClient.put<TaskFilterGroup[]>(
      `${APIM_SERVICE_URL}/groupOrderIndexBulkUpdate`,
      groups,
      {
        headers: {
          'Sys-UserID': userId,
        },
      }
    );

    return { success: true };
  } catch (e) {
    const error: Error = e as Error;
    return { success: false, message: error?.message || 'an error occurred' };
  }
}

export async function upsertTaskFilterGroup(
  data: TaskFilterGroup,
  userId: string
): Promise<{ success: boolean; message?: string } | undefined> {
  try {
    const id = data?.groupId;
    data.userId = userId;
    await dispatchClient.put<TaskFilterGroup>(
      `${APIM_SERVICE_URL}/taskfilters/group/${id}`,
      data,
      {
        headers: {
          'Sys-UserID': userId,
        },
      }
    );

    return { success: true };
  } catch (e) {
    const error: Error = e as Error;
    return { success: false, message: error?.message || 'an error occurred' };
  }
}

export async function deleteTaskFilter(userId: string, filterId: string) {
  return dispatchClient.delete(`${APIM_SERVICE_URL}/taskfilters/${filterId}`, {
    headers: {
      'Sys-UserID': userId,
    },
  });
}

export async function deleteTaskFilterGroup(userId: string, groupId: string) {
  return dispatchClient.delete(
    `${APIM_SERVICE_URL}/taskfilters/group/${groupId}`,
    {
      headers: {
        'Sys-UserID': userId,
      },
    }
  );
}

// from demo app
export async function postResource(
  taskId: string,
  resource: Resource,
  correlationId: string
): Promise<GeneralResponse> {
  const resourceResponse = await axios.post(
    `${APIM_SERVICE_TASKS_URL}/${taskId}/resource`,
    resource,
    {
      headers: {
        'x-correlationid': correlationId,
      },
    }
  );

  return resourceResponse.data;
}

export async function markInProgress(
  externalReferenceId: string,
  correlationId: string
): Promise<GeneralResponse> {
  return dispatchClient.put(
    `${APIM_SERVICE_TASKS_URL}/${externalReferenceId}/queue/wip_q`,
    '',
    {
      headers: {
        'x-correlationid': correlationId,
      },
    }
  );
}

export async function markComplete(
  externalReferenceId: string,
  correlationId: string
): Promise<GeneralResponse> {
  return dispatchClient.put(
    `${APIM_SERVICE_TASKS_URL}/${externalReferenceId}/queue/egress`,
    '',
    {
      headers: {
        'x-correlationid': correlationId,
      },
    }
  );
}

export async function setTaskLockStatus(
  taskId: string,
  status: TaskLockStatus,
  request?: TaskStatusRequest
) {
  return dispatchClient.post(
    `dispatch/service/tasks/${taskId}/status/${status}`,
    request
  );
}

export async function completeExpiredTasks(resourceId: string) {
  try {
    await dispatchClient.post(`${APIM_SERVICE_TASKS_URL}/completeExpired`, {
      resourceId: resourceId,
    });

    return { success: true };
  } catch (e) {
    const error: Error = e as Error;
    return { success: false, message: error?.message || 'an error occurred' };
  }
}

export async function getMethodOfInspections(tenantId: string) {
  invariant(tenantId, 'Missing tenantId');

  return dispatchClient.get<string[]>(`${APIM_SERVICE_MOI_URL(tenantId)}`, {
    headers: {
      'Request-ID': uuidv4(),
    },
  });
}

export async function forceReassignTasks({
  taskIds,
  resourceId,
  reasonCode,
}: {
  taskIds: string[];
  resourceId: string;
  reasonCode?: string;
}) {
  try {
    const forceReassign = await dispatchClient.post(
      `${APIM_SERVICE_TASKS_URL}/forceReassign`,
      {
        taskIds,
        resourceId,
        reasonCode,
      }
    );

    if (forceReassign.status === 200) {
      return { success: true };
    }

    return { success: false };
  } catch (e) {
    const error = e as { message?: string };
    return { success: false, message: error?.message || 'an error occurred' };
  }
}

export async function getUnassignedTaskCount() {
  try {
    const response = await dispatchClient.get(
      `${APIM_SERVICE_TASKS_URL}/unassigned/count`
    );

    if (response.status === 200) {
      return response.data;
    }

    return { count: 0 };
  } catch (e) {
    throw new Error('Error fetching unassigned count from API');
  }
}

export async function markTaskAsRead(taskId: string) {
  invariant(taskId, 'Missing taskId');

  const response = await dispatchClient.put<void>(
    `${APIM_SERVICE_TASKS_URL}/${taskId}/isReadDate`,
    {
      date: new Date().toISOString(),
    }
  );

  return response;
}

export async function getUnreadAssignedTasksCount(resourceId: string) {
  invariant(resourceId, 'Missing resourceId');

  const response = await dispatchClient.get(
    `${APIM_SERVICE_TASKS_URL}/badge/count`,
    {
      params: {
        resourceId: resourceId,
      },
    }
  );

  return response;
}

export function isTaskReassignable(task: ITaskMessage): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isTaskReAssignable =
    task.taskType === TaskType.NON_COMPLEX ||
    task.taskType === TaskType.COMPLEX_CHILD
      ? task.appointment && new Date(task.appointment) >= new Date()
      : task.taskType === TaskType.CLAIM_OWNER ||
        task.taskType === TaskType.COMPLEX ||
        (task.mustStartAfter && new Date(task.mustStartAfter) >= today);
  return isTaskReAssignable || false;
}

//cancel bulk tasks
type TaskStatusAction = Exclude<TaskStatus, 'notstarted'>;

export async function bulkCancelTasks(
  taskIds: string[],
  status: TaskStatusAction,
  reason?: string
) {
  return dispatchClient.post(`/dispatch/service/tasks/status/${status}`, {
    taskIds,
    status,
    reason,
  });
}

// Bulk lock tasks. Unlike bulkCancelTasks (which returns the raw AxiosResponse
// because callers ignore the body), the bulk lock endpoint returns a per-task
// summary body that the UI consumes -- so we unwrap response.data here.
export async function bulkLockTasks(
  taskIds: string[]
): Promise<BulkTaskLockResponse> {
  const response = await dispatchClient.post<BulkTaskLockResponse>(
    '/dispatch/service/tasks/status/locked',
    { taskIds }
  );
  return response.data;
}

export async function getCWSSummary(
  cwsNumber?: string
): Promise<CWSSummary | undefined> {
  try {
    const response = await dispatchClient.get<CWSSummary>(
      `${CWS_SUMMARY_URL(cwsNumber || '')}`
    );

    return response.data;
  } catch (e) {
    // 404 is the API's "no such claim" answer (e.g. claims created outside
    // CWS). Treat it as an empty result so it is not retried or logged as a
    // failure.
    if (axios.isAxiosError(e) && e.response?.status === 404) {
      return undefined;
    }
    throw new Error('Error fetching CWS Summary from API');
  }
}

export async function getQualifiedResourcesFNOLFeature(
  tenantId: string
): Promise<boolean | undefined> {
  invariant(tenantId, 'Missing tenantId, cannot fetch feature flag');

  const response = await dispatchClient.get<boolean>(
    `${APIM_SERVICE_QUALIFIED_RESOURCES_FNOL_URL(tenantId)}`,
    {
      headers: {
        'Request-ID': uuidv4(),
      },
    }
  );

  const value = response?.data;

  return value;
}

export async function getNaqSelfServiceFeature(
  tenantId: string
): Promise<boolean | undefined> {
  invariant(tenantId, 'Missing tenantId, cannot fetch feature flag');

  const response = await dispatchClient.get<boolean | string>(
    `${APIM_SERVICE_NAQ_SELF_SERVICE_URL(tenantId)}`,
    {
      headers: {
        'Request-ID': uuidv4(),
      },
    }
  );

  return String(response?.data).toLowerCase() === 'true';
}

// NAQ self-service retry. Returns the parsed NaqRetryResponse body. Axios
// throws for non-2xx; DispatchService returns 422 with the same NaqRetryResponse
// shape on rule-based failures, so callers should read failureReason from
// error.response.data in their onError handler.
export async function retryTask(taskId: string): Promise<NaqRetryResponse> {
  invariant(taskId, 'Missing taskId, cannot retry task');

  const response = await dispatchClient.post<NaqRetryResponse>(
    APIM_SERVICE_NAQ_RETRY_URL(taskId)
  );

  return response.data;
}

export async function getUIValidationRules(
  tenantId: string
): Promise<UIValidationRulesResponse | undefined> {
  invariant(tenantId, 'Missing tenantId, cannot fetch validation');

  const response = await dispatchClient.get<UIValidationRulesResponse>(
    `${APIM_SERVICE_UI_VALIDATION_RULES_URL(tenantId)}`,
    {
      headers: {
        'Request-ID': uuidv4(),
      },
    }
  );

  const value = response?.data;

  return value;
}

export async function getCollapseNonWorkingTimeConfig(
  tenantId: string
): Promise<CollapseNonWorkingTimeConfig | undefined> {
  invariant(tenantId, 'Missing tenantId, cannot fetch non working time config');

  const response = await dispatchClient.get<CollapseNonWorkingTimeConfig>(
    `${APIM_SERVICE_COLLAPSE_NON_WORKING_TIME_CONFIG_URL(tenantId)}`,
    {
      headers: {
        'Request-ID': uuidv4(),
      },
    }
  );

  const value = response?.data;

  return value;
}

export async function getTrimLeadingZerosFlag(
  tenantId: string
): Promise<boolean | undefined> {
  invariant(tenantId, 'Missing tenantId, cannot fetch non working time config');

  const response = await dispatchClient.get<boolean>(
    `${APIM_SERVICE_Trim_Leading_Zeros_URL(tenantId)}`,
    {
      headers: {
        'Request-ID': uuidv4(),
      },
    }
  );

  const value = response?.data;

  return value;
}

// =====================================================================
// ADO #93625 — Task notes, review status, note-categories admin (A+B+C)
// =====================================================================

export type TaskNoteDto = {
  noteId: string;
  categoryId?: string;
  categoryName?: string;
  body?: string;
  authoredBy?: string;
  authorType?: 'user' | 'system';
  authoredByUserId?: string;
  authoredAtUtc: string;
};

export type NoteCategoryDto = {
  categoryId: string;
  name: string;
  isRetired: boolean;
  retiredAtUtc?: string;
  createdAtUtc: string;
  createdBy?: string;
};

export type ReviewStatusState = 'NotReviewed' | 'Reviewed';

export type ReviewStatusDto = {
  taskId: string;
  state: ReviewStatusState;
  lastChangedBy?: string;
  lastChangedByUserId?: string;
  lastChangedAtUtc: string;
};

export type ReviewStatusHistoryItem = {
  fromState: ReviewStatusState;
  toState: ReviewStatusState;
  changedBy?: string;
  changedByUserId?: string;
  changedAtUtc: string;
};

export type RelatedTasksNotesDto = {
  taskId: string;
  taskType?: string;
  status?: string;
  relevantDateUtc?: string;
  notes: TaskNoteDto[];
};

export type RelatedNotesResponse = {
  results: RelatedTasksNotesDto[];
  truncated: boolean;
};

const NOTES_URL = (taskId: string) =>
  `${APIM_SERVICE_TASKS_URL}/${taskId}/notes`;
const NOTE_BY_ID_URL = (taskId: string, noteId: string) =>
  `${APIM_SERVICE_TASKS_URL}/${taskId}/notes/${noteId}`;
const NOTE_CATEGORIES_URL = () => `${APIM_SERVICE_URL}/notecategories`;
const NOTE_CATEGORY_URL = (categoryId: string) =>
  `${APIM_SERVICE_URL}/notecategories/${categoryId}`;
const REVIEW_STATUS_URL = (taskId: string) =>
  `${APIM_SERVICE_TASKS_URL}/${taskId}/reviewstatus`;
const REVIEW_STATUS_HISTORY_URL = (taskId: string) =>
  `${APIM_SERVICE_TASKS_URL}/${taskId}/reviewstatus/history`;
const CLAIM_NOTES_URL = (claimNumber: string) =>
  `${APIM_SERVICE_URL}/claims/${claimNumber}/notes`;
const REVIEW_STATUS_FEATURE_FLAG_URL = (tenantId: string) =>
  `configuration/${tenantId}/feature:review-status-enabled`;

export async function getNotesForTask(taskId: string): Promise<TaskNoteDto[]> {
  invariant(taskId, 'taskId required');
  const response = await dispatchClient.get<TaskNoteDto[]>(NOTES_URL(taskId));
  return response?.data ?? [];
}

export async function createOrReplaceNote(
  taskId: string,
  noteId: string,
  body: { categoryId?: string; body?: string }
): Promise<TaskNoteDto> {
  invariant(taskId, 'taskId required');
  invariant(noteId, 'noteId required');
  const response = await dispatchClient.put<TaskNoteDto>(
    NOTE_BY_ID_URL(taskId, noteId),
    body
  );
  return response.data;
}

export async function getNoteCategories(
  includeRetired = false
): Promise<NoteCategoryDto[]> {
  const response = await dispatchClient.get<NoteCategoryDto[]>(
    NOTE_CATEGORIES_URL(),
    { params: { active: !includeRetired } }
  );
  return response?.data ?? [];
}

export async function createNoteCategory(
  name: string
): Promise<NoteCategoryDto> {
  const response = await dispatchClient.post<NoteCategoryDto>(
    NOTE_CATEGORIES_URL(),
    { name }
  );
  return response.data;
}

export async function renameNoteCategory(
  categoryId: string,
  name: string
): Promise<NoteCategoryDto> {
  const response = await dispatchClient.put<NoteCategoryDto>(
    NOTE_CATEGORY_URL(categoryId),
    { name }
  );
  return response.data;
}

export async function retireNoteCategory(
  categoryId: string
): Promise<NoteCategoryDto> {
  const response = await dispatchClient.delete<NoteCategoryDto>(
    NOTE_CATEGORY_URL(categoryId)
  );
  return response.data;
}

export async function getReviewStatus(
  taskId: string
): Promise<ReviewStatusDto> {
  invariant(taskId, 'taskId required');
  const response = await dispatchClient.get<ReviewStatusDto>(
    REVIEW_STATUS_URL(taskId)
  );
  return response.data;
}

export async function setReviewStatus(
  taskId: string,
  state: ReviewStatusState
): Promise<ReviewStatusDto> {
  invariant(taskId, 'taskId required');
  const response = await dispatchClient.put<ReviewStatusDto>(
    REVIEW_STATUS_URL(taskId),
    { state }
  );
  return response.data;
}

export async function getReviewStatusHistory(
  taskId: string
): Promise<ReviewStatusHistoryItem[]> {
  invariant(taskId, 'taskId required');
  const response = await dispatchClient.get<ReviewStatusHistoryItem[]>(
    REVIEW_STATUS_HISTORY_URL(taskId)
  );
  return response?.data ?? [];
}

export async function getRelatedNotes(
  claimNumber: string,
  excludeTaskId?: string
): Promise<RelatedNotesResponse> {
  invariant(claimNumber, 'claimNumber required');
  const response = await dispatchClient.get<RelatedNotesResponse>(
    CLAIM_NOTES_URL(claimNumber),
    { params: excludeTaskId ? { excludeTaskId } : undefined }
  );
  return response.data;
}

export async function getReviewStatusFeatureFlag(
  tenantId: string
): Promise<boolean> {
  invariant(tenantId, 'tenantId required');
  try {
    const response = await dispatchClient.get<boolean | string>(
      REVIEW_STATUS_FEATURE_FLAG_URL(tenantId),
      { headers: { 'Request-ID': uuidv4() } }
    );
    // The flag is stored as a string ("true"/"false") so the backend's string-typed
    // FeatureToggle can read it; tolerate the legacy boolean too during config rollout.
    return String(response?.data).toLowerCase() === 'true';
  } catch {
    return false;
  }
}

export async function getBulkLockPaginationUpperLimit(
  tenantId: string
): Promise<BulkLockPaginationUpperLimitConfig | undefined> {
  invariant(
    tenantId,
    'Missing tenantId, cannot fetch bulk lock pagination upper limit'
  );

  const response = await dispatchClient.get<BulkLockPaginationUpperLimitConfig>(
    `${APIM_SERVICE_BULK_LOCK_PAGINATION_UPPER_LIMIT_URL(tenantId)}`,
    {
      headers: {
        'Request-ID': uuidv4(),
      },
    }
  );

  return response?.data;
}
